import { useState } from 'react';
import {
  HOME3_DESTINATION_LABEL_KEY,
  type Home3Destination,
  type Home3DestinationFilter,
  type Home3ReadState,
} from '../../../lib/home3-feed';
import { AVATAR_PALETTE, avatarInitials, avatarSlotFor } from '../../../lib/avatar-color';
import { categoryBadgeShown, prefersReducedMotion } from '../../../lib/category-badge-clear';
import { useBasePath } from '../../../lib/use-base-path';
import { useTranslation } from '../../../lib/i18n';
import { Avatar, ScrollArea, Typography, type TypographyVariant } from '../ui';
import styles from './Home3Categories.module.css';

/**
 * The category row — "All" and the six destinations, one tile each, on one line
 * (Figma `19697:59325`).
 *
 * It replaced a grid of six cards that each carried their destination's whole per-state breakdown.
 * The tiles say one thing instead: **how much is new since you last looked at that category**, as a
 * disc on the corner, which clears when you press it. A board that showed four counts per card asked
 * the reader to do the arithmetic; a single unread count is the question they actually have.
 *
 * ## A horizontal slider that always peeks the next tile (Figma 20077-39081)
 *
 * The tiles are a FIXED 60x60 (the frame's own size) in a horizontal `ScrollArea`, not equal shares of
 * the column — so on a phone, where seven 60+14 tiles overflow the width, the next tile is always
 * partly visible and the row reads as a slider you can push. The bar is `hidden` (the affordance is the
 * peeking tile, like `MobileAgentCards`); on a wide desktop column where all seven fit, there is simply
 * nothing to scroll. The label sits BELOW the tile box, not inside it — only the 60x60 box lifts/recedes
 * with selection.
 *
 * ## The art is the frame's, and only Messages is live
 *
 * Five of the six are illustrations exported from the frames into `public/illustrations/home3/` —
 * bespoke drawings rather than icons, which is the one case the icon rule exempts from lucide. They
 * are named from the TSX as `<img src>` off `useBasePath()`, never from the stylesheet: a `url()` in
 * a CSS module resolves against the CDN, where `public/` does not exist, and paints nothing with no
 * error (see the package CLAUDE.md).
 *
 * **Outreach draws the real person** (Figma 21792-39363): ONE face — whoever wrote most recently,
 * from the contact book, falling back to their initials and then to the design's placeholder photo —
 * with a neon presence dot on its corner. It is the one tile whose art is data.
 *
 * The phone tiles and the wide pills read the same art (Figma 21792-39317 / 21417-120837), and the
 * iOS and Android rows draw the same marks from their own copies of these vectors.
 *
 * ## On a wide pane the tiles are pills (Figma 21417-120837)
 *
 * Above the screen's fold the seven become one row of equal 50-tall pills, the art beside the label
 * (the stylesheet's `@container home3` block). The label steps up from Tiny 12/16 to Small 14/18 there,
 * which is a type ROLE and so a class, not something the container query can switch — hence `narrow`.
 *
 * The first option reads "Activity" at every width (Figma 21792-39317 names it so on the phone frame
 * too), as on the iOS and Android rows. The pills also carry the wide frame's "Outreach" for Messages.
 * Only the calendar pill has its own wide art (`wide/calendar.svg`).
 */
const ART: Record<Exclude<Home3Destination, 'outreach'>, string> = {
  tasks: 'tasks',
  routines: 'routines',
  goals: 'goals',
  apps: 'apps',
  files: 'files',
};

const WIDE_LABEL_KEY: Partial<Record<Home3Destination, string>> = {
  outreach: 'home.overview.filter.outreach',
};

/** Left to right, as the frame lays them out — not `HOME3_DESTINATIONS`' own order. */
export const HOME3_CATEGORY_ORDER: readonly Home3Destination[] = [
  'tasks',
  'routines',
  'goals',
  'outreach',
  'apps',
  'files',
];

/** Whoever wrote most recently — the Outreach mark's face. `seed` picks their colour off the avatar ramp. */
export type Home3Messenger = { name: string; photoUrl?: string; seed: string };

/**
 * A category's mark in its 40 box — the tiles' own art, and the one the canvas's entry points draw
 * (`Home3EntryPoints`), so the two doors into a category cannot wear different drawings.
 */
export function Home3CategoryArt({ destination, messenger }: { destination: Home3Destination; messenger?: Home3Messenger }) {
  const basePath = useBasePath();
  return (
    <span className={styles.art}>
      {destination === 'outreach' ? (
        /* 27 is off the DS avatar scale because the frame draws it there. The placeholder photo
           is only for nobody: a real sender with no photo gets their own initials. A ramp slot
           keyed on the person, never `brand`, which is the SIGNED-IN user's gradient. */
        <span className={styles.outreach}>
          <Avatar
            initials={messenger ? avatarInitials(messenger.name) : ''}
            photoUrl={messenger ? messenger.photoUrl : `${basePath}/illustrations/home3/messages-photo.png`}
            size={27}
            color={messenger ? avatarSlotFor(messenger.seed) : AVATAR_PALETTE[0]}
          />
          <span className={styles.outreachDot} />
        </span>
      ) : (
        <img src={`${basePath}/illustrations/home3/${ART[destination]}.svg`} alt="" width={40} height={40} />
      )}
    </span>
  );
}

export function Home3Categories({
  unread,
  selected,
  onSelect,
  onSelectAll,
  readState,
  listId,
  messengers = [],
  narrow = false,
  calendar,
}: {
  /** New-since-last-visit per category — see `useHome3CategoryVisits`. Zero means no disc. */
  unread: Record<Home3Destination, number>;
  selected: Home3DestinationFilter;
  /** The tile tapped. The caller selects it, and marks that category seen. */
  onSelect: (destination: Home3Destination) => void;
  /** The "All" tile — clears the category filter. */
  onSelectAll: () => void;
  /** Until `ready` no tile wears a disc: a count of 0 and "not known yet" are different facts. */
  readState: Home3ReadState;
  /** The list region the tiles filter, for `aria-controls`. */
  listId: string;
  /**
   * Whoever wrote most recently, newest first — the Outreach tile wears the first one's face. Empty
   * until the contacts read lands. `seed` is what picks their colour off the avatar ramp: their id
   * where there is one, so the colour survives a rename.
   */
  messengers?: Home3Messenger[];
  /** The pane is at or under the fold — the 60x60 tiles. Otherwise the wide pills. */
  narrow?: boolean;
  /** The Calendar tile, after Files. While it is chosen no category, and not "All", is. */
  calendar?: { selected: boolean; onSelect: () => void };
}) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  // Wide: Small/SemiBold for the chosen pill, Small/Medium for the rest (the frame's two roles).
  const allOn = selected === null && !calendar?.selected;
  const labelVariant = (on: boolean): TypographyVariant =>
    narrow ? (on ? 'tiny-semi-bold' : 'p3') : on ? 'h4' : 'small-medium';

  return (
    // A horizontal slider (Figma 20077-39081): the fixed 60x60 tiles overflow a phone's width, so the
    // next tile always peeks and the row reads as scrollable. `bar="hidden"` — the peeking tile is the
    // affordance, as on the other swipe rows. The radiogroup is the inner track so the scroller stays a
    // pure scroll container.
    <ScrollArea axis="horizontal" bar="hidden" className={styles.scroller}>
      <div className={styles.row} role="radiogroup" aria-label={t('home.overview.filterDestination')}>
      {/* "All" is the row's first option and now carries its own art — the stacked-cards mark the frame
          draws for it (Figma 19848-66034), over the same label the other tiles use. */}
      <button
        type="button"
        role="radio"
        aria-checked={allOn}
        aria-controls={listId}
        className={`${styles.cat}${allOn ? ` ${styles.catOn}` : ''}`}
        onClick={onSelectAll}
      >
        <span className={styles.tile}>
          <span className={styles.art}>
            {/* The frame's Activity mark is this same drawing, so the pill reuses it. */}
            <img src={`${basePath}/illustrations/home3/all.svg`} alt="" width={40} height={40} />
          </span>
        </span>
        {/* Same selected rule as the six below: "All" is the chosen tile whenever nothing else is. */}
        <Typography
          variant={labelVariant(allOn)}
          color={allOn ? 'primary' : 'secondary'}
          component="span"
          className={styles.label}
          noWrap
        >
          {t('home.overview.filter.activity')}
        </Typography>
      </button>

      {HOME3_CATEGORY_ORDER.map((destination) => {
        const on = selected === destination;
        const count = readState === 'ready' ? unread[destination] : 0;
        const name = t((!narrow && WIDE_LABEL_KEY[destination]) || HOME3_DESTINATION_LABEL_KEY[destination]);
        return (
          <button
            key={destination}
            type="button"
            role="radio"
            aria-checked={on}
            aria-controls={listId}
            className={`${styles.cat}${on ? ` ${styles.catOn}` : ''}`}
            onClick={() => onSelect(destination)}
          >
            <span className={styles.tile}>
            <Home3CategoryArt destination={destination} messenger={messengers[0]} />
            {/* The count the reader has not seen. It is the tile's own, so pressing one tile
                leaves the others' discs alone. */}
            <UnreadDisc count={count} />
            </span>
            {/* The CHOSEN tile's label is the primary ink at the SemiBold tier; the rest sit
                secondary and Regular (Design QA, Caro — Figma 20018:106262). The weight is a role
                either way, never a literal, so both carry the mobile restore. */}
            <Typography
              variant={labelVariant(on)}
              color={on ? 'primary' : 'secondary'}
              component="span"
              className={styles.label}
              noWrap
            >
              {name}
            </Typography>
          </button>
        );
      })}

      {/* The Calendar is a place rather than a feed category: it has no unread disc, and it opens the
          whole calendar under the tiles, the way the other tiles open their own page. */}
      {calendar && (
        <button
          type="button"
          role="radio"
          aria-checked={calendar.selected}
          aria-controls={listId}
          className={`${styles.cat}${calendar.selected ? ` ${styles.catOn}` : ''}`}
          onClick={calendar.onSelect}
        >
          <span className={styles.tile}>
            <span className={styles.art}>
              <img src={`${basePath}/illustrations/home3/${narrow ? '' : 'wide/'}calendar.svg`} alt="" width={40} height={40} />
            </span>
          </span>
          <Typography
            variant={labelVariant(calendar.selected)}
            color={calendar.selected ? 'primary' : 'secondary'}
            component="span"
            className={styles.label}
            noWrap
          >
            {t('calendar.title')}
          </Typography>
        </button>
      )}
      </div>
    </ScrollArea>
  );
}

/** The tile's unread disc. When the count drops to 0 it plays the clear animation with the last number
 *  (`lib/category-badge-clear.ts`), then unmounts on the animation's end. */
function UnreadDisc({ count }: { count: number }) {
  const [lastShown, setLastShown] = useState(count);
  const reducedMotion = count === 0 && lastShown > 0 && prefersReducedMotion();
  if (count > 0 && count !== lastShown) setLastShown(count);
  if (reducedMotion) setLastShown(0);
  const shown = categoryBadgeShown(count, lastShown, reducedMotion);
  if (shown === null) return null;
  const clearing = count === 0;
  return (
    <Typography
      variant="tiny-semi-bold"
      color="inherit"
      component="span"
      className={`${styles.badge}${clearing ? ` ${styles.badgeClearing}` : ''}`}
      aria-hidden={clearing || undefined}
      onAnimationEnd={clearing ? (e) => e.target === e.currentTarget && setLastShown(0) : undefined}
    >
      <span className={styles.badgeNumeral}>{shown}</span>
    </Typography>
  );
}
